import type { ReactNode } from 'react';
import { useDentroDaAba } from '@shared/components/Abas';

type Fundo = 'papel' | 'fundo' | 'destaque';

interface Props {
  id: string;
  /** Rótulo curto na margem. Deixa a página varrível sem ser lida. */
  rotulo?: string;
  titulo?: string;
  fundo?: Fundo;
  children: ReactNode;
}

const FUNDOS: Record<Fundo, string> = {
  papel: 'bg-papel',
  fundo: 'bg-papel-fundo',
  destaque: 'bg-destaque text-papel',
};

/**
 * O primitivo da página. Todo bloco é uma Section.
 *
 * Concentra o ritmo vertical, a largura da mancha, a âncora do menu e a
 * numeração de capítulo na margem. Mudou o respiro? Muda aqui e os blocos
 * obedecem.
 *
 * Dentro de uma aba, larga a própria largura e o próprio fundo: quem manda
 * passa a ser a moldura, e blocos vizinhos são separados por um fio em vez de
 * por uma faixa de cor.
 */
export function Section({ id, rotulo, titulo, fundo = 'papel', children }: Props) {
  const dentroDaAba = useDentroDaAba();
  const escuro = fundo === 'destaque';

  if (dentroDaAba) {
    return (
      <section id={id} data-bloco className="border-linha py-8 not-first:border-t">
        <div className="grade-bloco">
          <div className="margem-bloco">
            <span aria-hidden className="numero-bloco" />
            {rotulo ? <span className="rotulo">{rotulo}</span> : null}
          </div>
          <div className="max-w-2xl">
            {titulo ? <h2 className="display-secao">{titulo}</h2> : null}
            <div className={titulo ? 'mt-6' : ''}>{children}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id={id} data-bloco className={FUNDOS[fundo]}>
      <div className="mx-auto w-full max-w-5xl px-6 py-12 sm:px-10 sm:py-14">
        <div className="grade-bloco">
          <div className="margem-bloco">
            <span aria-hidden className="numero-bloco" />
            {rotulo ? (
              <span className={`rotulo ${escuro ? 'text-papel/75' : ''}`}>{rotulo}</span>
            ) : null}
          </div>

          <div className="max-w-2xl">
            {titulo ? <h2 className="display-secao">{titulo}</h2> : null}
            <div className={titulo ? 'mt-6' : ''}>{children}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
